<template>
  <!-- Login renders chrome-less; everything else lives inside the shared shell. -->
  <RouterView v-if="isBareRoute" />
  <AppShell v-else ref="shellRef" module="suite" :collapse-sidebar-label="t('accountMenu.collapseSidebar')" :expand-sidebar-label="t('accountMenu.expandSidebar')" :tabs-previous-label="t('common.tabsPrevious')" :tabs-next-label="t('common.tabsNext')">
    <template #sidebar>
      <Sidebar
        module="suite"
        :module-label="t('lumiModules.suite.name')"
        :module-icon="(LUMI_ICONS['home'] as Component)"
        :locked="isWorkspaceLocked"
        :logo-src="logoUrl"
        :home-href="workspaceUrl"
        :modules="isWorkspaceLocked ? [] : moduleTargets"
        :module-switcher-label="t('common.switchModule')"
        :module-list-heading="t('common.moduleListHeading')"
        :module-teasers="moduleTeasers"
        :module-teaser-heading="t('common.moduleTeaserHeading')"
        :module-teaser-tag-label="t('common.moduleTeaserTag')"
        :module-teaser-action-label="t('common.moduleTeaserAction')"
        :module-teaser-note="canManageBilling ? '' : t('common.moduleTeaserNote')"
        :module-switcher-close-label="t('common.close')"
        :items="navItems"
        :active-id="activeId"
        :name="stageName"
        :photo-url="stagePhotoUrl"
        :email="stageEmail"
        :signed-in-label="t('accountMenu.signedIn')"
        :role-label="stageRoleLabel || sidebarWorkspaceName || undefined"
        :role-menu-label="stageRoleLabel ? t('common.role') : t('workspace.title')"
        :locale="locale"
        :locales="localeOptions"
        :theme="theme"
        :logout-label="t('homebase.nav.logout')"
        :language-menu-label="t('homebase.nav.language')"
        :notifications-label="bellLabel"
        :notifications-count="unreadCount"
        :profile-href="profileHref"
        :profile-label="t('accountMenu.profile')"
        :settings-href="settingsHref"
        :settings-label="t('accountMenu.settings')"
        :theme-menu-label="t('accountMenu.theme')"
        :theme-light-label="t('accountMenu.themeLight')"
        :theme-dark-label="t('accountMenu.themeDark')"
        :back-label="t('accountMenu.back')"
        :forward-label="t('accountMenu.forward')"
        :can-go-back="canGoBack"
        :can-go-forward="canGoForward"
        :support-label="t('accountMenu.support')"
        @navigate="onNavigate"
        @settings="onSettings"
        :logout-disabled="onboardingStore.isDemo"
        :logout-hint="t('demoStage.logoutHint')"
        @back="goBack"
        @forward="goForward"
        @logout="handleLogout"
        @support="openSupport"
        @notifications="refreshUnread"
        @update:locale="onLocaleChange"
        @update:theme="onThemeChange"
      >
        <!-- The multi-tenant switcher used to live in the header (#468); it
             moves here, above the account row — never while a demo persona is
             worn (#409), same as before. The Lumi-token meter that sat beside
             it is gone (#544): usage lives on the admin AI page only. -->
        <template v-if="showTenantSwitcher" #footer-extra>
          <WorkspaceTenantSwitcher v-if="showTenantSwitcher" />
        </template>
        <template #notifications-panel="{ close }">
          <!-- The inbox is this app's own route (routed, not loaded) and a followed row may
               also resolve to an administration page of this origin or to the landing
               site, which only the workspace's own resolver knows. -->
          <NotificationsPopover
            :api="notificationApi"
            :demo="onboardingStore.isDemo"
            inbox-href="/notifications"
            :open-inbox="openInbox"
            :resolve-target="followTargetUrl"
            @close="close"
          />
        </template>
      </Sidebar>
    </template>
    <!-- The demo stage bar (#409 follow-ups, F2) frames the WHOLE app — above the
         sidebar, not beside it — so it takes the shell's full-width `#top` strip;
         `#banner` keeps the strips that belong inside the main column. Its CTA
         opens the setup wizard modal (?setup=1, issue 214) and reads "continue"
         once setup has begun. The bar's sign-out is this chrome's own handler
         (F13): the sidebar's button is disabled while the demo is on and points
         here. -->
    <template v-if="onboardingStore.isDemo" #top>
      <DemoStageBar :store="stageBarStore" :sign-out="handleLogout" :redirect="openSetupWizard" :identity="{ workspaceName: onboardingStore.companyName || activeTenantSlug, email: authStore.user?.email, userId: authStore.user?.userId, tenantSlug: activeTenantSlug, photoUrl: ownPhotoUrl }" :module-targets="moduleTargets" />
    </template>
    <!-- The billing banner's suspended strip is suppressed while the lock screen is up
         (issue 213): the full lock screen supersedes it, so we don't double up on
         "you're locked" messaging. The warning-stage banner is unaffected. -->
    <template v-if="(billingBanner && !isWorkspaceLocked) || (needsYouStrip && !isWorkspaceLocked) || supportAccessActive" #banner>
      <BillingBanner v-if="billingBanner && !isWorkspaceLocked" v-bind="billingBanner" />
      <NeedsYouStrip v-if="needsYouStrip && !isWorkspaceLocked" v-bind="needsYouStrip" />
      <!-- Consented temporary support access (ADR 0041): while a grant is live,
           the tenant's admins see it on every page — transparency that does not
           depend on anyone opening a notification. Links to the review console,
           where it can be ended. -->
      <div
        v-if="supportAccessActive"
        class="flex items-center justify-between gap-3 bg-warning-soft px-4 py-2 text-sm text-warning-text"
      >
        <span>{{ t('tenants.supportAccessBanner') }}</span>
        <Button variant="link" class="shrink-0" @click="openSupportAccessReview">
          {{ t('tenants.supportAccessReview') }}
        </Button>
      </div>
    </template>
    <!-- Payments lockout (issue 213 F23/F24/F26): a suspended/blocked tenant sees the
         full lock screen in place of the workspace, not dead module tiles that 404.
         The nav is greyed/disabled (below); the lock screen carries the support
         recovery path. -->
    <RouterView v-if="!isWorkspaceLocked" />
    <WorkspaceLocked v-else />

    <!-- Lumi's own imprint + privacy, from the one shared place (issue 221). It
         sits inside the scrolling content because AppShell has no footer region;
         it renders on the lock screen too, since the § 5 DDG duty does not pause
         when billing does. -->
    <LegalLinksFooter
      :landing-base-url="landingUrl"
      :tenant-notice-url="employeeNoticeFooterUrl"
      :tenant-notice-company="tenantsStore.activeTenant?.name"
    />

    <!-- Quick "contact support" dialog, opened from the sidebar footer button.
         Files a request for the active tenant and then says so, in place: it does
         NOT jump into the ticket it just created (#425). -->
    <SupportRequestDialog
      :open="supportOpen"
      :title="t('support.newTicketTitle')"
      :subject-label="t('support.subject')"
      :subject-placeholder="t('support.subjectPlaceholder')"
      :message-label="t('support.message')"
      :message-placeholder="t('support.messagePlaceholder')"
      :submit-label="t('support.send')"
      :cancel-label="t('common.cancel')"
      :busy="supportBusy"
      :submit-disabled="!supportConsent || !activeTenantSlug"
      :sent="supportSent"
      :sent-message="t('support.sent')"
      :done-label="t('support.goToMyRequests')"
      @done="goToMyRequests"
      @submit="submitSupport"
      @cancel="supportOpen = false"
    >
      <template #intro>{{ t('support.newTicketIntro') }}</template>
      <template #extra>
        <Checkbox v-model="supportConsent">{{ t('support.consentLabel') }}</Checkbox>
      </template>
    </SupportRequestDialog>

    <!-- Suite-wide Lumi (ADR 0027): one shared headless panel, app-injected
         transport. Mode/state/conversation persist per tenant+user so Lumi
         reappears where the user left it across module switches. -->
    <template #dock>
      <LumiDock :api="assistantApi" module="suite" :module-targets="moduleTargets" :identity="{ tenantSlug: activeTenantSlug || currentTenantSlug(), userId: lumiSubject, email: stageEmail, locale }" :suppress-global-error="true" :suppress-greeting="onboardingStore.isDemo" :open-on-arrival="onboardingStore.isDemo" />
    </template>
  </AppShell>
</template>

<script setup lang="ts">
import { computed, onMounted, onBeforeUnmount, provide, ref, watch, type Component } from 'vue';
import { RouterView, useRoute, useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { AppShell, Sidebar, SupportRequestDialog, Checkbox, BillingBanner, useTheme, LUMI_ICONS, Button } from '@lumi/ui';
import type { SidebarItem, SidebarNavItem, SidebarSection, ModuleSwitchTarget, ModuleTeaser, LumiModuleKey } from '@lumi/ui';
import { LumiDock, DemoStageBar, NotificationsPopover, createDemoGuestsApi, useApiMutation, useBillingBanner, useNeedsYouStrip, needsYouRefreshKey, NeedsYouStrip, useInAppHistory, LegalLinksFooter,
  personaModuleKeys, readDemoPersona, clearDemoPersonaRefusal, useOwnPhotoUrl,
} from '@lumi/app-kit';
import { useAuthStore } from '@/stores/auth';
import { useTenantsStore } from '@/stores/tenants';
import { useSignOut } from '@/composables/useSignOut';
import { createTicketBody, useSupportStore } from '@/stores/support';
import type { TicketDetail } from '@/stores/support';
import { useOnboardingStore } from '@/stores/onboarding';
import { notificationApi, assistantApi, supportApi, userApi, paymentApi, peopleSelfApi } from '@/utils/api';
import { setLocale, SUPPORTED_LOCALES } from '@/i18n';
import { runtimeWorkspaceUrl, currentTenantSlug } from '@/utils/runtime-config';
import { fetchStanding } from '@/utils/billing';
import { buildModuleSwitchTargets, accessibleModuleKeys, stageModuleKeys } from '@/utils/moduleSwitcher';
import { buildModuleTeasers, noteTenantUnavailability, teasableModuleKeys } from '@lumi/app-kit';
import { landingBaseUrl, moduleDeepLinkUrl } from '@/utils/module-links';
import { followTargetUrl } from '@/utils/notifications';
import {
  SETUP_PAGE_PATH,
  SETUP_SECTIONS,
  accessibleSetupModuleKeys,
  setupSurfaceEligible,
} from '@/utils/setupPanel';
import { useSetupSections } from '@/utils/setupEntriesAccess';
import { fetchPublicEmployeeNotice } from '@/utils/employeeNotice';
import { asksForkOnSetupEntry } from '@/utils/setupEntry';
import { STATUTORY_RULES_PATH, probeStatutoryRulesAccess, showsStatutoryRulesNav, tenantRunsTime } from '@/utils/statutoryRulesAccess';
import {
  TRAINING_OCCUPATIONS_PATH,
  probeTrainingOccupationsAccess,
  showsTrainingOccupationsNav,
  tenantRunsApprenticeship,
} from '@/utils/trainingOccupationsAccess';
import { licensedImportModules, probeAnyImportAccess } from '@/utils/dataImportAccess';
import { cachedAnyImportAccess } from '@/utils/dataImportAccessCache';
import WorkspaceTenantSwitcher from './WorkspaceTenantSwitcher.vue';
import WorkspaceLocked from './WorkspaceLocked.vue';
import logoUrl from '@/assets/lumi-hr-logo.svg';

const route = useRoute();
const router = useRouter();
const { canGoBack, canGoForward, back: goBack, forward: goForward } = useInAppHistory(router);
const { t, locale } = useI18n();
const { theme, setTheme } = useTheme();
const authStore = useAuthStore();
const tenantsStore = useTenantsStore();
const onboardingStore = useOnboardingStore();
// The workspace demo banner's CTA opens the setup wizard modal in place (issue 214).
//
// …unless the AVV has not been accepted yet (#332, #364 F7). This CTA is the ONE
// route from a live demo into setup that does not pass the fork, and the fork is
// where the acceptance gate lives — so from here the admin used to walk the whole
// wizard and have Finish refused with a 409 (`ONBOARDING_AVV_ACCEPTANCE_REQUIRED`),
// having never been asked to sign anything. Ask first, on the fork's AVV step, and
// let it hand back to the wizard. The server gate is untouched: this is the
// friendly path to it, not a replacement for it.
//
// …and unless nothing is staged yet, in which case this CTA opens the FORK
// (ADR 0032 amendment, 2026-09-01: "setup from demo asks the fork first"). The
// router guard enforces the same rule for every other entry (`/setup`, a module
// UI's deep link, a bookmark); routing straight there from here just avoids a
// visible bounce through `/?setup=1`. The fork owns the AVV gate for both of its
// branches, so the `?avv=1` handoff below is only needed on the staged path,
// which goes back into the wizard without passing the fork.
// Resetting the demo from the bar (#409 M2), with the module set chosen in the
// bar's confirmation (ADR 0083; none keeps the stored set). The write lives here,
// at its call site (issue 195 D5); the bar owns the confirmation. On success the
// status is `demo_seeding` and the fork's preparing screen takes over, saying it
// is a reset. The persona choice stays — the cast is the same people whatever
// the set — but a refusal this tab remembered is dropped, since a grown set may
// give the default its seat. A failure is `handled`: the bar renders its own
// line off the `{ ok: false }`.
// CONSUMER (DemoStageBar.confirm via `stageBarStore.rebuildDemo`): reads the
// outcome's `ok`; on false it shows `demoStage.rebuildFailed` inside the
// confirmation and keeps the dialog open, so the user sees "could not be rebuilt".
const { mutate: rebuildDemoMutation } = useApiMutation<boolean, [string[] | undefined]>({
  tier: 'inlineEdit',
  errorChannel: 'handled',
  run: async (modules) => {
    const tenantId = await onboardingStore.activeTenantId();
    if (!tenantId) return false;
    let res;
    try {
      res = await userApi.post(`${onboardingStore.base(tenantId)}/demo/reset`, modules ? { modules } : undefined);
    } catch (e) {
      // A guest revoked (or a demo ended) under this tab: land on the ended page.
      noteTenantUnavailability(e, tenantsStore.activeTenant?.slug, undefined, 'user-api');
      throw e;
    }
    onboardingStore.apply(res.data);
    clearDemoPersonaRefusal(tenantsStore.activeTenant?.slug);
    await router.replace('/onboarding');
    return true;
  },
});
// A finished journey's celebration, recorded once per user (#409 M4). The line
// on the bar is the visible result, so no toast; a failure is `handled` — best
// effort, the next journeys read says whether it stuck. The local copy is marked
// first so the line never shows twice in this tab.
// CONSUMER (DemoStageBar.celebrate via `stageBarStore.celebrateJourney`): nothing
// reads `outcome.ok`; the user sees the celebration line once either way, and a
// write that did not stick shows it once more on the next reload.
const { mutate: celebrateJourneyMutation } = useApiMutation<void, [string]>({
  tier: 'inlineEdit',
  errorChannel: 'handled',
  run: async (personId) => {
    const tenantId = await onboardingStore.activeTenantId();
    if (!tenantId) return;
    await userApi.post(`${onboardingStore.base(tenantId)}/journeys/celebrate`, { personId }, { suppressGlobalError: true });
  },
});
// The colleagues of the demo (issue 669): the bar's "Invite a colleague" dialog reads,
// sends and revokes through the same three user-api routes the module SPAs' shared
// store uses — one implementation, wired here to this app's own client and tenant.
const demoGuests = createDemoGuestsApi({ userApi, resolveTenantId: () => onboardingStore.activeTenantId() });
const stageBarStore = computed(() => ({
  ...demoGuests,
  isAdmin: onboardingStore.isAdmin,
  isGuest: onboardingStore.isGuest,
  workspaceCompanyName: onboardingStore.companyName || null,
  setupStarted: onboardingStore.setupStarted,
  stage: onboardingStore.stage,
  activePersona: onboardingStore.activePersona,
  setPersona: onboardingStore.setPersona,
  demoModules: onboardingStore.demoModules,
  demoModuleOffer: onboardingStore.demoModuleOffer,
  journeys: onboardingStore.journeys,
  fetchJourneys: onboardingStore.fetchJourneys,
  celebrateJourney: async (personId: string) => {
    onboardingStore.markCelebrated(personId);
    await celebrateJourneyMutation(personId);
  },
  rebuildDemo: async (_kind: 'reset', modules?: string[]) => {
    const outcome = await rebuildDemoMutation(modules);
    return { ok: outcome.ok && outcome.data === true };
  },
}));

function openSetupWizard() {
  if (
    asksForkOnSetupEntry({
      mode: onboardingStore.mode,
      isAdmin: onboardingStore.isAdmin,
      firstRunResolved: onboardingStore.firstRunResolved,
      hasStagedWork: onboardingStore.hasStagedWork,
    })
  ) {
    router.push({ path: '/onboarding' });
    return;
  }
  if (!onboardingStore.avvAccepted) {
    router.push({ path: '/onboarding', query: { avv: '1' } });
    return;
  }
  router.push({ path: '/', query: { setup: '1' } });
}

const workspaceUrl = runtimeWorkspaceUrl();
// The marketing site carrying Lumi's imprint/privacy pages (issue 221).
const landingUrl = landingBaseUrl();

// The doors, and the suite-onboarding fork, render chrome-less (full-screen). The
// setup wizard is a modal over the workspace (issue 214), so it is NOT a bare route.
//
// The door list is `meta.tenantless` — the flag the router already carries for the
// pages that work WITHOUT a resolved tenant, which is exactly the same set: login,
// forgot/reset, verify-email, invite acceptance, the e-mail-change confirmation.
// Reading the meta rather than re-matching paths here is what fixed #428: the
// invite's "set up your account" screen is a full-screen card that was being
// rendered INSIDE the workspace shell — a sidebar and a tenant header wrapped
// around a page for someone who has no session and no workspace yet — because the
// old predicate only knew the '/login' family by name. A door added later is now
// bare by virtue of being a door, not by being remembered here.
//
// Path matching is gone with it, and with it the trap it carried: a bare
// `startsWith('/login')` once swallowed '/login-branding', the workspace's own
// admin page for the employee sign-in screen (#364 F16).
const isBareRoute = computed(
  () => route.meta?.tenantless === true || route.path === '/onboarding',
);

// Admin-only chrome (the billing tab) needs both the admin
// role and a resolvable tenant slug (for X-Tenant-Slug).
const activeTenantSlug = computed(() => tenantsStore.activeTenant?.slug ?? '');
const isTenantAdmin = computed(
  () => !!activeTenantSlug.value && tenantsStore.hasAdminRole(tenantsStore.activeTenant),
);
// The tenant switcher moved from the header into the sidebar footer (#468): a
// multi-tenant member gets it there now; a single-tenant member gets nothing,
// same as the header gave them before. Off-stage only, like the rest of the
// account's own chrome (#409): the mask must never leak which real workspaces
// this person could switch to.
const showTenantSwitcher = computed(() => tenantsStore.isMultiTenant && !onboardingStore.isMasked);
// Holds the data_protection suite role (ADR 0033) — the raw flag, not the
// owner-OR-holder authority `isTenantAdmin` reads, so a holder who is neither
// owner nor suite_admin still sees the Datenverarbeitung entry (#430).
const holdsDataProtectionRole = computed(
  () => tenantsStore.activeTenant?.isDataProtection === true,
);
// privacy-api's D4 authority — owner OR data_protection holder — as user-api
// RESOLVED it (issue 789). The data subject request list is that authority's
// page; the flag is read, never rebuilt from the two above.
const isDataProtectionAuthority = computed(
  () => tenantsStore.activeTenant?.isDataProtectionAuthority === true,
);
// Billing (mandate setup, invoices, plan) is for the tenant OWNER or a holder of
// the `billing_manager` role, matching payment-api's TenantAdminGuard
// (membership && (isOwner || isBillingManager)). A generic module *_admin is NOT
// enough — that would show a Billing tab that 403'd for non-owner admins.
const canManageBilling = computed(
  () => !!activeTenantSlug.value && tenantsStore.canManageBilling(tenantsStore.activeTenant),
);
// The workspace's own employee notice (issue 576): workspace-ui IS the
// workspace, so it always knows both halves LegalLinksFooter's prop needs —
// its own base URL and the active tenant's slug — unlike the other UIs that
// render the shared footer (none do today; see LegalLinksFooter's own header
// comment). Empty slug (no tenant resolved yet) renders no link, the footer's
// own "no base URL → no link" rule extended to this prop.
//
// DEGRADE TO NO LINK: the link renders only once a silent probe of the same
// public read the notice page makes has answered for THIS slug — the login
// page's rule. A workspace with no published notice, or a privacy-api that
// cannot answer, gets no link rather than one into a 404 or a gated page.
const employeeNoticeProbedSlug = ref('');
const employeeNoticeFooterUrl = computed(() => {
  const slug = activeTenantSlug.value;
  if (!slug || employeeNoticeProbedSlug.value !== slug) return '';
  return `${runtimeWorkspaceUrl()}/legal/employee-privacy?tenantSlug=${encodeURIComponent(slug)}`;
});
watch(
  activeTenantSlug,
  async (slug) => {
    employeeNoticeProbedSlug.value = '';
    if (!slug) return;
    try {
      await fetchPublicEmployeeNotice(slug, locale.value === 'en' ? 'en' : 'de');
      // A tenant switch mid-probe must not light the link for the old slug.
      if (activeTenantSlug.value === slug) employeeNoticeProbedSlug.value = slug;
    } catch {
      /* no notice published, or unreachable — no link, silently */
    }
  },
  { immediate: true },
);

// Payments lockout (issue 213 F23/F24/F26): when the active tenant is suspended/
// blocked the whole workspace is inert — module data 404s, /billing 403s. Rather
// than dead tiles + cryptic not-found toasts + a silent Abrechnung bounce, the shell
// shows a full lock screen (WorkspaceLocked) with the support-recovery path and greys
// out the nav. In-app self-service pay-to-restore is NOT offered: payment-api resolves
// a suspended tenant to null, so the card/mandate-setup routes 403 by design —
// restoring access is a support action (letting the pay flow through the lockout needs
// a backend change, deferred to a follow-up). Driven off the tenants store, S7's
// status source, so the bar and the lock screen agree on when a workspace is locked.
const isWorkspaceLocked = computed(() => tenantsStore.isLocked(tenantsStore.activeTenant));

// The nav is disabled while the workspace is locked (issue 213): the CSS greys +
// blocks pointer events on the nav list, and this backstops it so a keyboard/programmatic
// navigate cannot slip a suspended member into a dead module route.
function onNavigate(id: string) {
  if (isWorkspaceLocked.value) return;
  router.push(id);
}

// App-wide billing banner (issue 213). The workspace renders the SAME bar as every
// module SPA, from the same composable and the same user-api billing-status read —
// it no longer derives its own from the tenants payload. Two consequences worth
// naming: the bar now reaches every member (not just billing managers), because
// with the read-only stage gone it is the last warning before lockout; and the
// manager/non-manager split comes from the endpoint's role flags rather than the
// local `canManageBilling`, so the bar and the /plan endpoints agree by
// construction. `fetchStanding` is still what makes the manager CTA cause-aware
// (fix-card vs review-plan) — the lightweight /plan/standing, NOT the full
// /plan/catalog, whose master→payment-api billing-summary fan-out is wasted when
// we only need standing.cause. Keyed on the active tenant (the surface is
// multi-tenant, so the slug rides per request).
//
// `tenantStatus` is the workspace's own obligation: this is the ONE app with a
// tenant switcher, so it is the one place a member can stand in front of a
// workspace the login refusal would have turned them away from. A member of an
// active tenant AND a suspended one signs in via the active one, then switches —
// GET /tenants does not filter the suspended workspace out of the switcher. The
// composable turns that into the locked bar; without it the switch lands on bare
// 404s with nothing on screen to explain them.
const { loadBillingStatus, billingBanner } = useBillingBanner({
  api: userApi,
  paymentApi,
  // A click on the banner's link while inside the workspace is a router navigation,
  // not a reload: BillingView reacts to a changed ?tab= / ?focus= query.
  navigateInApp: (path) => void router.push(path),
  workspaceUrl: runtimeWorkspaceUrl,
  fetchStanding,
  canLoad: () => !!authStore.token && !!activeTenantSlug.value,
  requestConfig: () => ({ headers: { 'X-Tenant-Slug': activeTenantSlug.value } }),
  tenantStatus: () => tenantsStore.activeTenant?.status ?? null,
  // First paint (issue 213 F25): `tenantStatus` reads the tenants store, which loads
  // lazily — on first paint of a suspended tenant it is null and the locked bar is
  // absent until a navigation refills it. The composable awaits this at the start of
  // every load, so the store is resolved by the time the bar mounts and the locked
  // bar shows immediately. Idempotent (the store no-ops once loaded).
  //
  // Only resolve the tenant-status source when there IS a session. On the chrome-less
  // /login route there is none, and an unguarded tenants load would 401 → the global
  // handler bounces to /login → the endless reload loop the bootstrap guard below
  // documents. Authenticated first paint still loads it, so F25's locked bar is
  // immediate (issue 213).
  ensureTenantStatus: () => (authStore.isAuthenticated ? tenantsStore.load() : Promise.resolve()),
});
watch(activeTenantSlug, () => void loadBillingStatus(), { immediate: true });

// Suite-wide legal-notice banner (issue 576 M3): loaded once per chrome mount
// alongside the billing status above, on the same tenant-switch watcher — this
// is the one app with a tenant switcher, so the active tenant's slug can change
// without a navigation. Slug-keyed like billing-status, same requestConfig.
// A demo has nothing for the strip (user-api's own rule, issue 795) — but entering
// one is not a tenant switch, so the answer read before it would stay up until the
// next read (issue 1055). Not asking in a demo clears that answer at once, and the
// watcher below asks again whenever the demo state flips.
const inDemo = computed(() => onboardingStore.isDemo || onboardingStore.isSeeding);
const { loadNeedsYou, needsYouStrip } = useNeedsYouStrip({
  api: userApi,
  workspaceUrl: runtimeWorkspaceUrl,
  canLoad: () => !!authStore.token && !!activeTenantSlug.value && !inDemo.value,
  requestConfig: () => ({ headers: { 'X-Tenant-Slug': activeTenantSlug.value } }),
  billingHref: () => `${runtimeWorkspaceUrl().replace(/\/$/, '')}/billing`,
  identity: () => ({ tenantSlug: activeTenantSlug.value, userId: authStore.user?.userId }),
});
watch([activeTenantSlug, inDemo], () => void loadNeedsYou(), { immediate: true });
// Lets the "Legal notices" setup screen — below this chrome in the router-view
// tree — ask for an immediate re-check right after closing the last open blank
// (issue 620), rather than waiting for the next tenant switch or the poll's
// own backstop window.
provide(needsYouRefreshKey, () => loadNeedsYou({ fresh: true }));

// Consented temporary support access (ADR 0041 §9). Only a workspace admin can
// see (or end) a grant, so only they are asked for it — a plain member's chrome
// makes no extra request. Best-effort: a failed read simply shows no banner
// rather than blocking the page, and the notification + mail already reached the
// admins independently.
const supportAccessActive = ref(false);
async function loadSupportAccess() {
  const tenantId = tenantsStore.activeTenant?.id;
  if (!tenantId || !tenantsStore.hasAdminRole(tenantsStore.activeTenant!)) {
    supportAccessActive.value = false;
    return;
  }
  try {
    const { data } = await userApi.get(`/access/tenants/${tenantId}/support-access`, { suppressGlobalError: true });
    supportAccessActive.value = !!data?.active;
  } catch {
    supportAccessActive.value = false;
  }
}
watch(() => tenantsStore.activeTenant?.id, () => void loadSupportAccess(), { immediate: true });

// The onboarding status is per TENANT, and switching tenant in the picker is not a
// navigation — so without this the router guard's status (demo banner, setup CTA)
// stayed on the tenant we just left, and the banner offered to set up the wrong
// workspace (#364 F9a). Skipped on the chrome-less /login route, which has no
// session to ask with.
watch(
  () => tenantsStore.activeTenant?.id,
  (id) => {
    if (id && authStore.isAuthenticated) void onboardingStore.fetchStatus(true);
  },
);

// Statutory rules (ADR 0091): the workspace admin's, and a Time `compliance.manage`
// holder's — which only time-api knows. Asked once per user and tenant for the
// session (utils/statutoryRulesAccess.ts keeps the answer, the route guard shares
// it), and only where it can be yes: a tenant that runs Time, a viewer who is not
// the workspace admin already, off stage.
const statutoryRulesViewer = computed(() => {
  const tenant = tenantsStore.activeTenant;
  return {
    userId: authStore.user?.userId,
    tenantSlug: tenant?.slug,
    isWorkspaceAdmin: tenantsStore.hasAdminRole(tenant),
    runsTime: tenantRunsTime(tenant),
  };
});
watch(
  () => [statutoryRulesViewer.value, onboardingStore.isMasked, tenantsStore.dataVersion] as const,
  ([viewer, masked]) => {
    if (masked || !viewer.runsTime || viewer.isWorkspaceAdmin || !viewer.tenantSlug) return;
    if (!authStore.isAuthenticated) return;
    void probeStatutoryRulesAccess(viewer.userId, viewer.tenantSlug);
  },
  { immediate: true },
);

// Training occupations (ADR 0092): the workspace admin's, and a `manage` holder's
// of the apprenticeship module (the head of training) — which only apprentice-api
// knows. The same shape as the statutory rules above: asked once per user and
// tenant for the session (utils/trainingOccupationsAccess.ts keeps the answer, the
// route guard shares it), and only where it can be yes: a tenant that runs the
// module, a viewer who is not the workspace admin already, off stage.
const trainingOccupationsViewer = computed(() => {
  const tenant = tenantsStore.activeTenant;
  return {
    userId: authStore.user?.userId,
    tenantSlug: tenant?.slug,
    isWorkspaceAdmin: tenantsStore.hasAdminRole(tenant),
    runsApprenticeship: tenantRunsApprenticeship(tenant),
  };
});
watch(
  () => [trainingOccupationsViewer.value, onboardingStore.isMasked, tenantsStore.dataVersion] as const,
  ([viewer, masked]) => {
    if (masked || !viewer.runsApprenticeship || viewer.isWorkspaceAdmin || !viewer.tenantSlug) return;
    if (!authStore.isAuthenticated) return;
    void probeTrainingOccupationsAccess(viewer.userId, viewer.tenantSlug);
  },
  { immediate: true },
);

// Daten-Import (ADR 0062): the owner and a suite admin see it as before; so does
// anybody who holds "Daten importieren" in at least one booked module — which only
// the modules know. One descriptor probe per booked module, once per user and
// tenant for the session and only for somebody who is not a workspace admin
// already (utils/dataImportAccess.ts keeps the answer in memory). UI only: every
// module API still decides on every request.
const dataImportViewer = computed(() => {
  const tenant = tenantsStore.activeTenant;
  return {
    userId: authStore.user?.userId,
    tenantSlug: tenant?.slug,
    modules: licensedImportModules(tenant),
  };
});
watch(
  () => [dataImportViewer.value, isTenantAdmin.value, onboardingStore.isMasked, tenantsStore.dataVersion] as const,
  ([viewer, admin, masked]) => {
    if (masked || admin || !viewer.tenantSlug || viewer.modules.length === 0) return;
    if (!authStore.isAuthenticated) return;
    void probeAnyImportAccess(viewer.userId, viewer.tenantSlug, viewer.modules);
  },
  { immediate: true },
);
const showsDataImportNav = computed(
  () =>
    isTenantAdmin.value ||
    cachedAnyImportAccess(dataImportViewer.value.userId, dataImportViewer.value.tenantSlug) === true,
);

// The setup entry: the same answer the Suite Home panel and the setup page read
// (utils/setupEntriesAccess.ts keeps one per user and tenant for the session).
const { sections: setupSections } = useSetupSections({
  eligible: () =>
    setupSurfaceEligible({
      tenant: tenantsStore.activeTenant,
      firstRunResolved: onboardingStore.firstRunResolved,
      isDemo: onboardingStore.isDemo,
      isMasked: onboardingStore.isMasked,
    }) && authStore.isAuthenticated,
  userId: () => authStore.user?.userId,
  tenantSlug: () => tenantsStore.activeTenant?.slug,
  bookedModules: () => {
    const mods = tenantsStore.activeTenant?.modules ?? [];
    return SETUP_SECTIONS.flatMap((s) => (s.source.kind === 'module' ? [s.source.moduleKey] : [])).filter((key) =>
      mods.some((m) => m.moduleKey === key && (m.status === 'active' || m.status === 'trial')),
    );
  },
  accessibleModules: () => accessibleSetupModuleKeys(tenantsStore.activeTenant as any),
  suiteAdmin: () => tenantsStore.hasAdminRole(tenantsStore.activeTenant),
  version: () => tenantsStore.dataVersion,
});

// The review console is an in-app route now that the admin pages live here (#432):
// a plain router.push, no cross-host session handoff to build.
function openSupportAccessReview() {
  router.push('/admin/support-access');
}

// The Administration group (#432): every workspace-level admin surface in ONE
// collapsible section, in the order a workspace admin reaches for them. Billing,
// the login screen and data import were already here as loose top-level entries;
// the five that arrived from the retired tenant admin app joined them rather than
// starting a second list somewhere else.
//
// The gates stay PER ITEM, not per section: billing is the billing-manager set,
// everything else the owner ∨ suite_admin set that user-api's ensureTenantAdmin
// admits — so the section shows exactly what this viewer may open, and is not
// rendered at all when that is nothing. `masked` is the demo persona (#409): on
// stage these are the real user's concerns, not the persona's.
const administrationGroup = computed<SidebarSection | null>(() => {
  const masked = onboardingStore.isMasked;
  if (masked) return null;
  const items: SidebarNavItem[] = [];
  if (isTenantAdmin.value) {
    items.push({ id: '/admin/sso', label: t('homebase.nav.sso'), icon: LUMI_ICONS['sign-in'] as Component });
    // Consented temporary support access (ADR 0041) — the same owner ∨ suite_admin
    // set the route's own fail-closed guard checks.
    items.push({ id: '/admin/support-access', label: t('homebase.nav.supportAccess'), icon: LUMI_ICONS['support'] as Component });
    // External AI agents (ADR 0103 rule 12): the same owner ∨ suite_admin set the
    // route's fail-closed guard checks. An agent connection is an integration of
    // the member's own choosing, so it carries that meaning's glyph (ADR 0096).
    items.push({ id: '/admin/external-agents', label: t('homebase.nav.externalAgents'), icon: LUMI_ICONS['integration'] as Component });
    items.push({ id: '/admin/audit', label: t('homebase.nav.audit'), icon: LUMI_ICONS['audit-log'] as Component });
    // The workspace's own employee login screen — the same owner ∨ suite_admin set
    // user-api's ensureTenantAdmin lets through on the endpoints behind it.
    items.push({ id: '/login-branding', label: t('loginBranding.nav'), icon: LUMI_ICONS['branding'] as Component });
  }
  // Datenverarbeitung (#430): the workspace-wide export and the blocked-erasure
  // list, moved out of "my data" — an admin surface, not a personal right, so it
  // belongs in this section. A suite admin OR a data_protection role holder, the
  // same OR the route's own guard enforces; unlike the items above it is NOT
  // folded into the plain `isTenantAdmin` branch, because a data_protection
  // holder who is neither owner nor suite_admin must still see this one entry.
  // Data subject requests (issue 789): the DSAR work list the reminder mail
  // links, for privacy-api's D4 authority — the owner OR the data_protection
  // holder, the same resolved set the route guard admits. Not the admin set:
  // a suite_admin who is neither is refused by privacy-api.
  if (isDataProtectionAuthority.value) {
    items.push({ id: '/admin/data-requests', label: t('homebase.nav.dataRequests'), icon: LUMI_ICONS['dsar'] as Component });
  }
  if (isTenantAdmin.value || holdsDataProtectionRole.value) {
    items.push({ id: '/admin/data-processing', label: t('homebase.nav.dataProcessing'), icon: LUMI_ICONS['privacy'] as Component });
    // Legal notices (issue 576): the employee privacy & AI notice, same gate
    // as Datenverarbeitung — the data_protection role holder answers for this
    // one too, whether or not they are also owner/suite_admin.
    items.push({ id: '/admin/legal-notices', label: t('homebase.nav.legalNotices'), icon: LUMI_ICONS['legal'] as Component });
  }
  // Statutory rules (ADR 0091): where the workspace deviates from working-time
  // law. Only with Time, for the workspace admin or whoever time-api admitted —
  // the same decision the route guard takes.
  if (showsStatutoryRulesNav(statutoryRulesViewer.value)) {
    items.push({ id: STATUTORY_RULES_PATH, label: t('homebase.nav.statutoryRules'), icon: LUMI_ICONS['statutory-rules'] as Component });
  }
  // Training occupations (ADR 0092): which recognised training occupations the
  // workspace offers. Only with the apprenticeship module, for the workspace
  // admin or whoever apprentice-api admitted — the same decision the route guard
  // takes.
  if (showsTrainingOccupationsNav(trainingOccupationsViewer.value)) {
    items.push({ id: TRAINING_OCCUPATIONS_PATH, label: t('homebase.nav.trainingOccupations'), icon: LUMI_ICONS['occupation'] as Component });
  }
  if (canManageBilling.value) {
    items.push({ id: '/billing', label: t('homebase.nav.billing'), icon: LUMI_ICONS['billing'] as Component });
    // AI usage (#665 D10): the billing set — owner ∨ billing_manager ∨ suite_admin —
    // the same set system-admin-api's /ai-usage/* admits. A billing manager is
    // asked to pay for the AI quota, so they see what it is spent on.
    items.push({ id: '/admin/ai-usage', label: t('homebase.nav.aiUsage'), icon: LUMI_ICONS['ai-usage'] as Component });
  }
  if (showsDataImportNav.value) {
    // Central data import/export hub (ADR 0062): the owner/suite admin, and whoever
    // holds "Daten importieren" in a booked module. Every action inside is
    // additionally gated per module, server-side.
    items.push({ id: '/data-import', label: t('dataImportHub.nav'), icon: LUMI_ICONS['import-export'] as Component });
  }
  // "Einrichten": the full list of setup screens (the page behind the Suite Home
  // panel's footer link), last in the group, exactly for a person who may open at
  // least one of them — and never in a demo, for a support grant or before first
  // run is resolved (masked already returned above).
  if (setupSections.value.length > 0) {
    items.push({ id: SETUP_PAGE_PATH, label: t('homebase.nav.setup'), icon: LUMI_ICONS['checklist'] as Component });
  }
  return items.length ? { section: t('homebase.nav.administration'), icon: LUMI_ICONS['module.admin'] as Component, items } : null;
});

// Nav mirrors the workspace's real routes.
const navItems = computed<SidebarItem[]>(() => {
  const items: SidebarItem[] = [
    { id: '/', label: t('homebase.nav.home'), icon: LUMI_ICONS['home'] as Component },
    { id: '/notifications', label: t('notifications.nav'), icon: LUMI_ICONS['notification'] as Component },
    { id: '/support', label: t('support.title'), icon: LUMI_ICONS['support'] as Component },
  ];
  // On stage (#409) the real workspace's concerns — the person's own account and
  // data rights, and the whole Administration group — are not offered: they are
  // the real user's, and the mask is taken off in the bar to reach them.
  const masked = onboardingStore.isMasked;
  // #317 A31 — /account existed as a route and as nothing else. It is where a
  // person links or removes an SSO sign-in method (user-api's three
  // `/me/identities` endpoints), so with no nav edge those endpoints were
  // reachable only by typing the URL, and "I can't get my Google login off this
  // account" had no answer in the product. Unconditional for the same reason
  // data-protection is: it is the person's own account, not a permission.
  if (!masked) items.push({ id: '/account', label: t('account.title'), icon: LUMI_ICONS['sign-in'] as Component });
  items.push({ id: '/preferences', label: t('homebase.preferences'), icon: LUMI_ICONS['settings'] as Component });
  // The Administration section (#432): the five tenant admin pages that used to be
  // their own app, plus the workspace-owned entries that always lived here. A
  // SECTION, not a quicklink — these are this app's own routes now.
  const admin = administrationGroup.value;
  if (admin) items.push(admin);
  return items;
});

// The nav is no longer flat (#432): a section's children are what `activeId`
// matches against, so they have to be walked out of it first — a section object
// has no `id`, and reading one off it made every nested route look inactive.
const flatNavItems = computed<SidebarNavItem[]>(() => {
  const out: SidebarNavItem[] = [];
  for (const item of navItems.value) {
    if ('section' in item) out.push(...item.items);
    else out.push(item);
  }
  return out;
});

const activeId = computed(() => {
  const ids = flatNavItems.value.map((i) => i.id);
  const matches = ids.filter((id) => route.path === id || (id !== '/' && route.path.startsWith(`${id}/`)));
  // '/' only matches exactly; longer paths win.
  const exact = route.path === '/' ? '/' : matches.sort((a, b) => b.length - a.length)[0];
  return exact ?? '';
});

const localeLabels: Record<string, string> = {
  de: 'DE', en: 'EN', fr: 'FR', es: 'ES', nl: 'NL', pl: 'PL', sv: 'SV', it: 'IT', pt: 'PT',
};
const localeOptions = computed(() =>
  SUPPORTED_LOCALES.map((code) => ({ value: code, label: localeLabels[code] || code.toUpperCase() })),
);

function onLocaleChange(value: string) {
  if (!value) return;
  locale.value = value;
  setLocale(value);
}

function onThemeChange(value: string) {
  if (value === 'light' || value === 'dark') setTheme(value);
}

// One sign-out path (composables/useSignOut): the sidebar's button, the demo
// stage bar's menu and the chrome-less fork all run this.
const { signOut: handleLogout } = useSignOut();

// Sidebar footer "Support" button → quick create-ticket dialog for the active
// tenant (mirrors the /support page's create flow; opens from anywhere).
const supportStore = useSupportStore();
const supportOpen = ref(false);
const supportConsent = ref(false);

// The request landed and the dialog says so, in place (#425).
const supportSent = ref(false);

// TIER `inlineEdit`: the dialog turns into its own receipt, so a toast would only
// repeat the sentence already on screen. A failure has nowhere in-place to go here
// — this dialog has no error slot — so it keeps the global channel.
//
// It used to navigate into the freshly created ticket, which is what #425 is
// about: a send is not a request to go somewhere, and being moved to a page you
// did not ask for is a poor substitute for being told it worked.
const { pending: supportBusy, mutate: openTicket } = useApiMutation<TicketDetail, [string, string, string]>({
  tier: 'inlineEdit',
  run: async (slug, subject, message) => {
    const { data } = await supportApi.post(
      '/tickets',
      createTicketBody(subject, message),
      supportStore.tenantCfg(slug),
    );
    return supportStore.adoptTicket(data as TicketDetail);
  },
});

function openSupport() {
  supportConsent.value = false;
  supportSent.value = false;
  supportOpen.value = true;
}

async function submitSupport(payload: { subject: string; message: string }) {
  const slug = activeTenantSlug.value;
  if (!slug || !supportConsent.value) return;
  const outcome = await openTicket(slug, payload.subject, payload.message);
  if (!outcome.ok) return;
  supportSent.value = true;
}

// The receipt's one button — and the only navigation this flow performs, because
// the person pressed it. Lands on their own requests, not on the new one.
function goToMyRequests() {
  supportOpen.value = false;
  supportSent.value = false;
  void router.push('/support');
}

// Module switcher: peer-module targets gated by the active tenant's access.
// Derived reactively from the shared tenants store (not a separate fetch keyed on
// currentTenantSlug — that races on the bare/dev host, where the slug is only known
// after the store resolves, and left the switcher empty). Admin is not a user-api
// module — it's layered in from the tenant's admin role.
const switcherSlug = computed(() => tenantsStore.activeTenant?.slug ?? currentTenantSlug());
const accessKeys = computed<LumiModuleKey[] | null>(() => accessibleModuleKeys(tenantsStore.activeTenant));
// The mask (#409): while a persona is worn the switcher and the quicklinks
// offer only the modules the persona holds a seat in — of those this workspace
// has — and no Admin: members, billing and ownership are off stage, reached by
// taking the mask off in the bar. The sidebar shows the persona, not the user.
const stagePersona = computed(() => onboardingStore.activePersona);
const stageEmail = computed(() => stagePersona.value?.email ?? authStore.user?.email ?? '');
// Whose Lumi conversation this is (issue 698): the signed-in user's id, or —
// while a demo persona is worn — a stable persona key, so each persona keeps a
// conversation of its own as it did when the dock keyed on the persona's e-mail.
const lumiSubject = computed(() => (stagePersona.value ? `persona:${stagePersona.value.personId}` : authStore.user?.userId ?? ''));
const stageName = computed(() => (stagePersona.value ? `${stagePersona.value.firstName} ${stagePersona.value.lastName}` : ''));
// The account row's face: the worn persona's generated portrait wins; outside the
// demo stage it is the signed-in person's own People photo (issue 796), read as the
// REAL user (`peopleSelfApi` carries no persona header). Neither = the initials.
const { ownPhotoUrl } = useOwnPhotoUrl({
  peopleApi: peopleSelfApi,
  getKey: () => (authStore.token && activeTenantSlug.value && authStore.user?.userId ? `${activeTenantSlug.value}:${authStore.user.userId}` : null),
});
const stagePhotoUrl = computed(() => stagePersona.value?.photoUrl || ownPhotoUrl.value || undefined);
const stageRoleLabel = computed(() => {
  const p = stagePersona.value;
  if (!p) return null;
  const key = `demoStage.persona.${p.key}`;
  const role = t(key);
  return role === key ? p.key : role;
});
// Single-tenant users get no switcher dropdown (spec §8 artboard): the workspace
// name they'd otherwise lose moves under the account row's name, via the
// Sidebar's own `role-label` slot. Multi-tenant users keep the dropdown as their
// one place to see/change the workspace name, so this stays empty for them.
// On stage the persona's role wins and the real workspace name must never leak
// onto the demo stage, so this is gated on `isMasked` explicitly rather than
// relying on `stageRoleLabel` being truthy whenever the stage is up.
// The account-menu row this value lands in is captioned for what it holds (#570):
// the persona's role on stage, the workspace otherwise. It used to carry the
// Sidebar's English default, "Role", above a workspace name, in every locale.
const sidebarWorkspaceName = computed(() =>
  onboardingStore.isMasked || tenantsStore.isMultiTenant ? '' : tenantsStore.activeTenant?.name ?? '',
);
// The persona's seats meet what the WORKSPACE has on stage, not the real user's
// own access: a demo guest holds no module assignment (issue 764).
const stageKeys = computed<LumiModuleKey[] | null>(() => stageModuleKeys(tenantsStore.activeTenant));
const switcherKeys = computed<LumiModuleKey[] | null>(() =>
  stagePersona.value
    ? personaModuleKeys(stagePersona.value).filter((k) => stageKeys.value?.includes(k))
    : accessKeys.value,
);
const storedDemoPersona = computed(() =>
  onboardingStore.isDemo ? readDemoPersona(switcherSlug.value, authStore.user?.userId) : null,
);
// The workspace-admin gate (ADR 0033), and NOT while a demo persona is worn: the
// Administration section and the account menu's Einstellungen both hang off it.
const moduleTargets = computed<ModuleSwitchTarget[]>(() =>
  buildModuleSwitchTargets(
    { token: authStore.token, refreshToken: authStore.refreshToken, tenantSlug: switcherSlug.value, locale: locale.value, demoPersona: storedDemoPersona.value },
    switcherKeys.value,
    workspaceUrl,
  ),
);

// The account menu's two suite entries (issue 401, spec §4): "Einstellungen" and
// "Mein Profil", fed the LIVE session so a locale or theme switch reaches them
// like it reaches the switcher (#364 F27). Neither while the workspace is locked:
// the nav is inert then, and an anchor would slip past onNavigate's guard.
//
// They are not the same KIND of link. "Mein Profil" is a cross-app deep link into
// people-ui, gated on `people` among the accessible keys. "Einstellungen" is the
// person's own preferences (#1069) — a route of THIS app, pushed on this router
// (see onSettings) like people-ui's "Mein Profil": no cross-origin handoff, no
// token in the URL, no full reload. Tenant administration is the Administration
// section of this nav, not an account-menu entry.
const suiteLinkContext = computed(() => {
  if (isWorkspaceLocked.value || !switcherSlug.value) return null;
  return {
    session: { token: authStore.token, refreshToken: authStore.refreshToken },
    context: { tenantSlug: switcherSlug.value, locale: locale.value, theme: theme.value, demoPersona: storedDemoPersona.value },
  };
});
const settingsHref = computed(() => {
  if (!suiteLinkContext.value) return '';
  return router.resolve('/preferences').href;
});
function onSettings(event: MouseEvent) {
  // Same contract as the profile entry: the app that OWNS the route keeps the
  // anchor real (middle-click, "open in new tab") and pushes on a plain click.
  if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
  if (!settingsHref.value) return;
  event.preventDefault();
  void router.push('/preferences');
}
const profileHref = computed(() => {
  const link = suiteLinkContext.value;
  if (!link || !switcherKeys.value?.includes('people')) return '';
  return moduleDeepLinkUrl('people', '/me', link.session, link.context) ?? '';
});


// The rest of the offering (issue 261) — sellable modules this workspace does not
// have. Off the workspace's entitlement rows, NOT off `accessKeys`, which also
// applies per-user assignment: a workspace that owns a module this user is not
// assigned to must not be teased it. `null` (access unconfirmed) yields no
// teasers, matching the switcher's fail-closed rule, and `moduleTargets` is
// passed in so nothing can appear as both an owned tile and a teaser.
//
// Locked workspaces get neither: the whole nav is inert while payments are in
// lockout, and an upsell on a suspended workspace would be absurd.
const teaserKeys = computed<LumiModuleKey[] | null>(() =>
  teasableModuleKeys(tenantsStore.activeTenant),
);
// NO UPSELLS WHILE THE DEMO IS RUNNING (#409). The showroom seeds every module
// whether or not the workspace booked it, so a demo is already showing the whole
// suite — an "add this module" tile beside modules the visitor is at that moment
// exploring for free reads as a price tag on something they were just given, and
// on a workspace that has not decided to buy anything yet it is the wrong ask at
// the wrong moment. The entitlements are unchanged: this hides the pitch, not the
// door, and the tiles come back the moment the demo ends.
const moduleTeasers = computed<ModuleTeaser[]>(() =>
  isWorkspaceLocked.value || onboardingStore.isDemo
    ? []
    : buildModuleTeasers(teaserKeys.value, moduleTargets.value, {
        // Same-app link, so no session handoff — this IS the workspace.
        billingHref: canManageBilling.value ? '/billing?tab=plans#plan-add' : '',
      }),
);

// Notification bell — only when a tenant slug is resolvable (the summary call
// needs X-Tenant-Slug; a bare multi-tenant chooser hides the bell). Driven by the
// reactive active tenant, not currentTenantSlug() (non-reactive localStorage) —
// otherwise the bell never appears once the store resolves the slug mid-session.
const bellLabel = computed(() => (activeTenantSlug.value ? t('notifications.nav') : undefined));
const unreadCount = ref(0);
let unreadTimer: ReturnType<typeof setInterval> | null = null;
async function refreshUnread() {
  if (!authStore.token || !currentTenantSlug()) return;
  try {
    const { data } = await notificationApi.get('/notifications/summary', { suppressGlobalError: true, params: onboardingStore.isDemo ? { demo: 1 } : undefined });
    unreadCount.value = data?.unread ?? 0;
  } catch {
    /* non-critical */
  }
}
const onNotifChanged = () => void refreshUnread();
// Whether this is a demo arrives with the onboarding status, after the first badge read:
// the count is asked again so it matches the list, which leaves the checkout lines out (issue 803).
watch(() => onboardingStore.isDemo, () => void refreshUnread());
function openInbox(href: string) {
  void router.push(href);
}

// Close the mobile drawer on navigation.
const shellRef = ref<InstanceType<typeof AppShell> | null>(null);
watch(() => route.path, () => shellRef.value?.close());

// The tenant slug only resolves after the store loads (bare/dev host). Fetch the
// unread count as soon as it does, so the bell's badge isn't stuck until the
// 60s poll ticks.
watch(activeTenantSlug, (slug) => {
  if (slug) void refreshUnread();
});

// Authenticated session bootstrap (tenants + notifications). MUST NOT run on the
// chrome-less /login route: there is no session there, so `GET /tenants` and the
// notifications poll would 401 and the global 401 handler would bounce the page
// back to /login — an endless reload loop. Under single-login every module sends
// an unauthenticated visitor here, so this path is hit constantly. Bootstrap once
// we're on a real (authenticated) route, whether we land there directly or arrive
// after signing in.
let sessionBootstrapped = false;
function bootstrapSession() {
  if (sessionBootstrapped) return;
  sessionBootstrapped = true;
  void tenantsStore.load();
  void refreshUnread();
  unreadTimer = setInterval(() => void refreshUnread(), 60_000);
}

onMounted(() => {
  // Only bootstrap when there IS a session. Guarding on auth (not the route) is
  // robust to the app mounting before the initial navigation resolves — otherwise
  // route.path is still '/' here even when we're really loading /login.
  if (authStore.isAuthenticated) bootstrapSession();
  window.addEventListener('lumi:notifications-changed', onNotifChanged);
});
// A session appears — signed in on /login, or a token handed off via the router
// (?token / SSO ticket). Bootstrap once.
watch(
  () => authStore.isAuthenticated,
  (authed) => {
    if (authed) bootstrapSession();
  },
);
onBeforeUnmount(() => {
  if (unreadTimer) clearInterval(unreadTimer);
  window.removeEventListener('lumi:notifications-changed', onNotifChanged);
});
</script>
